import type { Locale } from "@/shared/i18n/locale";

export interface WorkshopOptionContent {
  id: string;
  title: string;
  text: string;
  chips: string[];
  items: string[];
}

export interface MetricItemContent {
  className?: string;
  icon: string;
  label: string;
  prefix?: string;
  value: string;
}

export interface FallbackTestimonialContent {
  company: string;
  author: string;
  role: string;
  quote: string;
}

export interface HomeContent {
  nav: {
    results: string;
    cases: string;
    method: string;
    education: string;
    articles: string;
    scheduleMeeting: string;
    openMenu: string;
    closeMenu: string;
    homeAriaLabel: string;
    languageAriaLabel: string;
    portugueseAria: string;
    englishAria: string;
  };
  hero: {
    titlePart1: string;
    titleHighlight: string;
    subtitle: string;
    viewCases: string;
    talkToUs: string;
    partnersLabel: string;
  };
  results: {
    tag: string;
    title: string;
    subtitle: string;
    metrics: MetricItemContent[];
  };
  cases: {
    tag: string;
    title: string;
    subtitle: string;
    previousAriaLabel: string;
    nextAriaLabel: string;
    clientListAriaLabel: string;
    readFullCase: string;
  };
  approach: {
    tag: string;
    title: string;
    subtitle: string;
    consultingHeading: string;
    consultingText: string;
    technologyHeading: string;
    technologyText: string;
    coreText: string;
    badgePeople: string;
    badgeProcesses: string;
    badgeSystems: string;
    footnote: string;
  };
  method: {
    tag: string;
    title: string;
    subtitle: string;
    steps: {
      iconName: string;
      title: string;
      description: string;
    }[];
    poweredLabel: string;
    poweredText: string;
    poweredAccent: string;
  };
  workshops: {
    leadTextPart1: string;
    leadAccent: string;
    leadTextPart2: string;
    tag: string;
    title: string;
    introText: string;
    cta: string;
    ctaAccent: string;
    buttonText: string;
    customProgramNote: string;
    options: WorkshopOptionContent[];
  };
  articles: {
    tag: string;
    title: string;
    subtitle: string;
    substackText: string;
    substackButton: string;
    readArticleAria: string;
    publishedOn: string;
  };
  testimonials: {
    eyebrow: string;
    title: string;
    subtitle: string;
    ariaLabel: string;
    prevAria: string;
    nextAria: string;
    fallbacks: FallbackTestimonialContent[];
  };
  contact: {
    tag: string;
    headingPart1: string;
    headingAccent: string;
    headingPart2: string;
    text: string;
    namePlaceholder: string;
    nameAria: string;
    companyPlaceholder: string;
    companyAria: string;
    emailPlaceholder: string;
    emailAria: string;
    rolePlaceholder: string;
    roleAria: string;
    phonePlaceholder: string;
    phoneAria: string;
    interestLabel: string;
    interestOptions: {
      workshop: string;
      consultoria: string;
      treinamento: string;
      projeto: string;
    };
    challengePlaceholder: string;
    challengeAria: string;
    honeypotLabel: string;
    submitIdle: string;
    submitLoading: string;
    successMessage: string;
    errorMessage: string;
  };
  footer: {
    tagline: string;
    siteHeading: string;
    officesHeading: string;
    copyright: string;
    privacyPolicy: string;
    privacyPreferences: string;
    openMapsAria: (city: string) => string;
  };
}

export const homeContentPt: HomeContent = {
  nav: {
    results: "Resultados",
    cases: "Cases",
    method: "Nosso método",
    education: "Educação",
    articles: "Artigos",
    scheduleMeeting: "Agendar conversa",
    openMenu: "Abrir menu",
    closeMenu: "Fechar menu",
    homeAriaLabel: "AutoU — página inicial",
    languageAriaLabel: "Selecionar idioma",
    portugueseAria: "Português",
    englishAria: "Inglês",
  },
  hero: {
    titlePart1: "Sua empresa pronta para a era da ",
    titleHighlight: "Inteligência Artificial.",
    subtitle:
      "Implementação e capacitação por quem gera resultados nos maiores negócios do mundo.",
    viewCases: "Ver cases",
    talkToUs: "Falar com a gente",
    partnersLabel: "Nossos parceiros",
  },
  results: {
    tag: "Resultados",
    title: "O que já entregamos",
    subtitle: "Transformamos estratégia, dados e tecnologia em resultados mensuráveis.",
    metrics: [
      {
        icon: "/assets/ganhos-e-economias.svg",
        label: "Ganhos e economias geradas",
        prefix: "R$",
        value: "60MM+",
      },
      {
        icon: "/assets/paises-atingidos.svg",
        label: "Soluções em",
        value: "15 países",
      },
      {
        icon: "/assets/pessoas-impactadas.svg",
        label: "Pessoas impactadas",
        value: "+150 mil",
      },
    ],
  },
  cases: {
    tag: "Cases",
    title: "Problemas reais. Empresas que evoluíram.",
    subtitle:
      "Uma coleção de cases em que a IA e Automação se tornaram uma vantagem competitiva em grandes empresas.",
    previousAriaLabel: "Case anterior",
    nextAriaLabel: "Próximo case",
    clientListAriaLabel: "Clientes",
    readFullCase: "Ler case completo",
  },
  approach: {
    tag: "Nossa abordagem",
    title:
      "Consultoria que direciona. Tecnologia que resolve. Educação que multiplica.",
    subtitle: "Unimos aquilo que o mercado trata de modo separado.",
    consultingHeading: "Consultoria",
    consultingText: "Estratégia sem tecnologia fica presa no passado.",
    technologyHeading: "Tecnologia",
    technologyText: "Tecnologia sem estratégia fica subutilizada.",
    coreText: "Resultado",
    badgePeople: "Pessoas",
    badgeProcesses: "Processos",
    badgeSystems: "Sistemas",
    footnote:
      "O resultado real nasce quando pessoas, processos e sistemas se conectam.",
  },
  method: {
    tag: "Como trabalhamos",
    title: "Do problema ao resultado em semanas. Entregamos com agilidade",
    subtitle:
      "Identificamos as causas raiz do problema e construímos a solução certa, com evolução visível a cada ciclo.",
    steps: [
      {
        iconName: "Search",
        title: "Discovery",
        description:
          "Mapeamos o processo, identificamos os gargalos invisíveis e definimos a arquitetura da solução.",
      },
      {
        iconName: "Settings",
        title: "Build",
        description:
          "Projetamos e construímos a solução personalizada, integrada aos seus sistemas, com sprints quinzenais.",
      },
      {
        iconName: "Rocket",
        title: "Launch",
        description:
          "A plataforma vai ao ar com treinamento e suporte completo para sua equipe adotar com confiança.",
      },
      {
        iconName: "LineChart",
        title: "Evolve",
        description:
          "Sua solução continua evoluindo com o seu negócio. Refinamento, expansão e novos módulos a cada ciclo.",
      },
    ],
    poweredLabel: "Powered by",
    poweredText:
      "Aura é a plataforma própria da AutoU que conecta discovery, build e evolução em um único ambiente. ",
    poweredAccent: "Mais velocidade em cada etapa do método.",
  },
  workshops: {
    leadTextPart1: "Além de entregar resultados, também ",
    leadAccent: "ensinamos",
    leadTextPart2: " o seu time como entregar",
    tag: "Workshops e treinamentos",
    title:
      "Aprenda com quem implementa IA e gera resultados nos maiores negócios do mundo.",
    introText:
      "Entenda onde estão as maiores oportunidades da sua operação, capacite seu time e construa um plano claro para evoluir com IA e automação.",
    cta: "Escolha o que mais faz sentido para ",
    ctaAccent: "a sua organização.",
    buttonText: "Quero aprender",
    customProgramNote:
      "Cada programa é desenvolvido sob medida para o seu contexto, do formato ao conteúdo. Nossos especialistas estão prontos para ajudar seu time a dar o próximo passo na jornada de IA.",
    options: [
      {
        id: "imersao",
        title: "Mapeamento e Priorização",
        text: "Antes de investir em IA, automações ou novos sistemas, é preciso ter clareza sobre como sua empresa funciona hoje. Mapeamos processos, identificamos gargalos, alinhamos diferentes áreas e revelamos onde estão as maiores oportunidades de ganho para o negócio.",
        chips: ["Workshops", "Mapeamento"],
        items: [
          "Visão completa dos processos de ponta a ponta",
          "Identificação de gargalos e desperdícios ocultos",
          "Priorização das iniciativas de maior impacto",
          "Alinhamento entre equipes e áreas de negócio",
          "Decisões baseadas em dados e não em percepções",
        ],
      },
      {
        id: "capacitacao",
        title: "Capacitação em IA e Automação",
        text: "Transformamos descobertas em direção. Capacitamos sua equipe para utilizar IA no dia a dia, cocriamos soluções para desafios reais e estruturamos um roadmap claro de implementação. Sua empresa sai com mais conhecimento, mais alinhamento e um plano concreto para evoluir.",
        chips: ["Treinamentos", "Cocriação"],
        items: [
          "Equipes preparadas para aplicar IA na prática",
          "Roadmap estratégico com prioridades definidas",
          "Redução de riscos em novos investimentos",
          "Prototipação de soluções antes da implementação",
          "Maior velocidade para transformar ideias em resultados",
        ],
      },
    ],
  },
  articles: {
    tag: "Artigos",
    title: "O que estamos construindo e aprendendo",
    subtitle:
      "Uma coleção de artigos e aprendizados sobre como usamos tecnologia, dados e IA para resolver problemas reais.",
    substackText: "Acesse todos artigos no nosso perfil no Substack",
    substackButton: "Acessar",
    readArticleAria: "Ler artigo",
    publishedOn: "Publicado em",
  },
  testimonials: {
    eyebrow: "DEPOIMENTOS",
    title: "Provas de um modelo que funciona",
    subtitle: "Confira o que alguns de nossos parceiros disseram sobre a gente.",
    ariaLabel: "Depoimentos de clientes",
    prevAria: "Depoimentos anteriores",
    nextAria: "Próximos depoimentos",
    fallbacks: [
      {
        company: "WLM",
        author: "Eduardo Loyola",
        role: "Business Intelligence",
        quote:
          "Construímos uma solução personalizada e funcional, a parceria fortaleceu nossa iniciativa de inovação.",
      },
      {
        company: "L'Oréal",
        author: "Jean Farias",
        role: "Recursos Humanos",
        quote:
          "A equipe é ótima de se trabalhar, sempre atenta a resolver qualquer problema no sistema.",
      },
      {
        company: "Prudential",
        author: "Romulo Carmo",
        role: "Franquias",
        quote:
          "Obrigado pelo trabalho, time. A solução ficou exatamente como planejamos e a automação já está rodando.",
      },
      {
        company: "Fiat",
        author: "Mateus Flecha",
        role: "VP de Auditoria e Compliance",
        quote:
          "Reconhecimento mais que merecido por um resultado excelente. Um grande parabéns aos colegas que lideraram essa transformação e colocaram essa automação em prática, agora expandida para vários países. Obrigado à equipe da AutoU.",
      },
      {
        company: "L'Oréal",
        author: "Nathalia Florindo",
        role: "Global HR Project Manager",
        quote:
          "Esse app é incrível! É muito bom ter mais soluções digitais, vocês mandaram bem! Ter esses KPIs disponíveis automaticamente é demais. 🥳",
      },
      {
        company: "Nestlé",
        author: "Marie Lambert",
        role: "Innovation & Project Manager",
        quote:
          "Há anos tentávamos rastrear e validar notas fiscais dessa forma e nunca conseguimos. Agora conseguimos acompanhar cada nota individualmente com IA e gerar dados reais de cada processo que rodamos.",
      },
    ],
  },
  contact: {
    tag: "Vamos conversar",
    headingPart1: "Pronto para ",
    headingAccent: "transformar",
    headingPart2: " oportunidades em resultados?",
    text: "Fale conosco. Em 30 minutos, entendemos o seu momento para mostrarmos o caminho certo.",
    namePlaceholder: "Seu nome",
    nameAria: "Seu nome",
    companyPlaceholder: "Empresa",
    companyAria: "Empresa",
    emailPlaceholder: "E-mail corporativo",
    emailAria: "E-mail corporativo",
    rolePlaceholder: "Cargo",
    roleAria: "Cargo",
    phonePlaceholder: "Celular / WhatsApp (opcional)",
    phoneAria: "Celular / WhatsApp (opcional)",
    interestLabel: "Interesse",
    interestOptions: {
      workshop: "Workshop",
      consultoria: "Consultoria",
      treinamento: "Treinamento",
      projeto: "Projeto de IA",
    },
    challengePlaceholder: "Principal desafio (opcional)",
    challengeAria: "Principal desafio",
    honeypotLabel: "Não preencha este campo",
    submitIdle: "Agendar conversa",
    submitLoading: "Enviando...",
    successMessage:
      "Recebemos seu contato! Em breve nosso time retorna pelo e-mail informado.",
    errorMessage:
      "Não foi possível enviar agora. Tente novamente em instantes.",
  },
  footer: {
    tagline:
      "Inteligência artificial implementada e ensinada sob medida para grandes empresas. Não vendemos software, entregamos resultados.",
    siteHeading: "Site",
    officesHeading: "Escritórios",
    copyright: "© 2026 AutoU. Todos os direitos reservados.",
    privacyPolicy: "Declaração de privacidade",
    privacyPreferences: "Preferencias de privacidade",
    openMapsAria: (city: string) =>
      `Abrir escritório AutoU em ${city} no Google Maps`,
  },
};

export const homeContentEn: HomeContent = {
  nav: {
    results: "Results",
    cases: "Cases",
    method: "Our method",
    education: "Education",
    articles: "Articles",
    scheduleMeeting: "Schedule a meeting",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    homeAriaLabel: "AutoU — home page",
    languageAriaLabel: "Select language",
    portugueseAria: "Portuguese",
    englishAria: "English",
  },
  hero: {
    titlePart1: "Get your company ready for the ",
    titleHighlight: "AI era.",
    subtitle:
      "Implementation and training by teams delivering results for the world's largest enterprises.",
    viewCases: "View cases",
    talkToUs: "Talk to us",
    partnersLabel: "Our partners",
  },
  results: {
    tag: "Results",
    title: "What we have delivered",
    subtitle:
      "We transform strategy, data, and technology into measurable business results.",
    metrics: [
      {
        icon: "/assets/ganhos-e-economias.svg",
        label: "Gains and savings generated",
        prefix: "R$",
        value: "60MM+",
      },
      {
        icon: "/assets/paises-atingidos.svg",
        label: "Solutions across",
        value: "15 countries",
      },
      {
        icon: "/assets/pessoas-impactadas.svg",
        label: "People impacted",
        value: "+150k",
      },
    ],
  },
  cases: {
    tag: "Cases",
    title: "Real problems. Companies that evolved.",
    subtitle:
      "A collection of cases where AI and automation became a true competitive advantage for enterprise leaders.",
    previousAriaLabel: "Previous case",
    nextAriaLabel: "Next case",
    clientListAriaLabel: "Clients",
    readFullCase: "Read full case",
  },
  approach: {
    tag: "Our approach",
    title:
      "Consulting that guides. Technology that solves. Education that multiplies.",
    subtitle: "We unite what the market treats in silos.",
    consultingHeading: "Consulting",
    consultingText: "Strategy without technology remains trapped in the past.",
    technologyHeading: "Technology",
    technologyText: "Technology without strategy remains underutilized.",
    coreText: "Result",
    badgePeople: "People",
    badgeProcesses: "Processes",
    badgeSystems: "Systems",
    footnote:
      "Real results happen when people, processes, and systems connect.",
  },
  method: {
    tag: "How we work",
    title: "From problem to result in weeks. Delivered with agility",
    subtitle:
      "We identify the root causes of the problem and build the right solution, with visible progress in every cycle.",
    steps: [
      {
        iconName: "Search",
        title: "Discovery",
        description:
          "We map the process, uncover invisible bottlenecks, and design the solution architecture.",
      },
      {
        iconName: "Settings",
        title: "Build",
        description:
          "We design and build the customized solution, integrated with your systems, in bi-weekly sprints.",
      },
      {
        iconName: "Rocket",
        title: "Launch",
        description:
          "The platform goes live with full training and support for your team to adopt it with confidence.",
      },
      {
        iconName: "LineChart",
        title: "Evolve",
        description:
          "Your solution keeps evolving with your business. Refinement, expansion, and new modules in every cycle.",
      },
    ],
    poweredLabel: "Powered by",
    poweredText:
      "Aura is AutoU's proprietary platform connecting discovery, build, and evolution in a unified environment. ",
    poweredAccent: "Greater speed across every stage of the method.",
  },
  workshops: {
    leadTextPart1: "Beyond delivering results, we also ",
    leadAccent: "teach",
    leadTextPart2: " your team how to deliver",
    tag: "Workshops and training",
    title:
      "Learn from the team implementing AI and delivering results for the world's largest enterprises.",
    introText:
      "Understand where the greatest opportunities lie in your operation, upskill your team, and build a clear plan to evolve with AI and automation.",
    cta: "Choose what makes the most sense for ",
    ctaAccent: "your organization.",
    buttonText: "I want to learn",
    customProgramNote:
      "Each program is tailored to your context, from format to curriculum. Our specialists are ready to help your team take the next step on the AI journey.",
    options: [
      {
        id: "imersao",
        title: "Mapping and Prioritization",
        text: "Before investing in AI, automation, or new systems, you need clarity on how your company works today. We map processes, identify bottlenecks, align departments, and reveal where the greatest business gains lie.",
        chips: ["Workshops", "Mapping"],
        items: [
          "Complete end-to-end visibility into processes",
          "Identification of hidden bottlenecks and waste",
          "Prioritization of high-impact initiatives",
          "Alignment across teams and business units",
          "Decisions driven by data rather than perception",
        ],
      },
      {
        id: "capacitacao",
        title: "AI and Automation Enablement",
        text: "We turn discovery into direction. We upskill your team to use AI daily, co-create solutions for real-world challenges, and structure a clear implementation roadmap. Your company gains more knowledge, tighter alignment, and a concrete path forward.",
        chips: ["Training", "Co-creation"],
        items: [
          "Teams prepared to apply AI in daily practice",
          "Strategic roadmap with defined priorities",
          "De-risking new technology investments",
          "Prototyping solutions before deployment",
          "Faster velocity from ideas to tangible results",
        ],
      },
    ],
  },
  articles: {
    tag: "Articles",
    title: "What we are building and learning",
    subtitle:
      "A collection of articles and insights on how we use technology, data, and AI to solve real-world problems.",
    substackText: "Access all articles on our Substack profile",
    substackButton: "Visit",
    readArticleAria: "Read article",
    publishedOn: "Published on",
  },
  testimonials: {
    eyebrow: "TESTIMONIALS",
    title: "Proof of a model that works",
    subtitle: "Check out what some of our partners have said about us.",
    ariaLabel: "Client testimonials",
    prevAria: "Previous testimonials",
    nextAria: "Next testimonials",
    fallbacks: [
      {
        company: "WLM",
        author: "Eduardo Loyola",
        role: "Business Intelligence",
        quote:
          "We built a customized, functional solution; the partnership strengthened our innovation initiative.",
      },
      {
        company: "L'Oréal",
        author: "Jean Farias",
        role: "Human Resources",
        quote:
          "The team is wonderful to work with, always attentive to solving any system issues.",
      },
      {
        company: "Prudential",
        author: "Romulo Carmo",
        role: "Franchises",
        quote:
          "Thank you for the work, team. The solution turned out exactly as planned and the automation is already up and running.",
      },
      {
        company: "Fiat",
        author: "Mateus Flecha",
        role: "VP of Audit & Compliance",
        quote:
          "Well-deserved recognition for an outstanding result. A huge congratulations to the colleagues who led this transformation and put this automation into practice, now expanded across multiple countries. Thank you to the AutoU team.",
      },
      {
        company: "L'Oréal",
        author: "Nathalia Florindo",
        role: "Global HR Project Manager",
        quote:
          "This app is incredible! It's so great to have more digital solutions, you guys did an amazing job! Having these KPIs available automatically is awesome. 🥳",
      },
      {
        company: "Nestlé",
        author: "Marie Lambert",
        role: "Innovation & Project Manager",
        quote:
          "For years we tried to track and validate invoices this way without success. Now we can monitor each invoice individually with AI and generate real data from every process we run.",
      },
    ],
  },
  contact: {
    tag: "Let's talk",
    headingPart1: "Ready to ",
    headingAccent: "transform",
    headingPart2: " opportunities into results?",
    text: "Get in touch. In 30 minutes, we understand your stage to show the right path forward.",
    namePlaceholder: "Your name",
    nameAria: "Your name",
    companyPlaceholder: "Company",
    companyAria: "Company",
    emailPlaceholder: "Work email",
    emailAria: "Work email",
    rolePlaceholder: "Job title / Role",
    roleAria: "Job title / Role",
    phonePlaceholder: "Mobile / WhatsApp (optional)",
    phoneAria: "Mobile / WhatsApp (optional)",
    interestLabel: "Interest",
    interestOptions: {
      workshop: "Workshop",
      consultoria: "Consulting",
      treinamento: "Training",
      projeto: "AI Project",
    },
    challengePlaceholder: "Main challenge (optional)",
    challengeAria: "Main challenge",
    honeypotLabel: "Do not fill this field",
    submitIdle: "Schedule a meeting",
    submitLoading: "Sending...",
    successMessage:
      "We received your message! Our team will get back to you shortly via the provided email.",
    errorMessage:
      "Unable to send right now. Please try again in a few moments.",
  },
  footer: {
    tagline:
      "Custom artificial intelligence implemented and taught for enterprise businesses. We don't sell software, we deliver results.",
    siteHeading: "Site",
    officesHeading: "Offices",
    copyright: "© 2026 AutoU. All rights reserved.",
    privacyPolicy: "Privacy statement",
    privacyPreferences: "Privacy preferences",
    openMapsAria: (city: string) =>
      `Open AutoU office in ${city} on Google Maps`,
  },
};

export function getHomeContent(locale: Locale): HomeContent {
  return locale === "en" ? homeContentEn : homeContentPt;
}
